import {
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  CircularProgress
} from '@mui/material';
import { Box } from '@mui/system';
import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
// react icons
import { FaRegUser } from 'react-icons/fa';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { GiMoneyStack, GiPayMoney, GiTakeMyMoney } from 'react-icons/gi';
import { HiReceiptTax } from 'react-icons/hi';
import { MdOutlineDateRange, MdOutlineMoreHoriz, MdOutlineSendToMobile } from 'react-icons/md';
import { TbReceiptTax } from 'react-icons/tb';
import { BiCloudUpload } from 'react-icons/bi';
// material icons
import AddCardIcon from '@mui/icons-material/AddCard';
import AdUnitsIcon from '@mui/icons-material/AdUnits';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CloseIcon from '@mui/icons-material/Close';
import EventNoteIcon from '@mui/icons-material/EventNote';
import GroupIcon from '@mui/icons-material/Group';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { fDate } from '../../utils/formatTime';
import { NOT_AVAILABLE } from '../Global/Constants';
// componenets
import BrudCrumbs from '../../components/BreadCrumbs';
import Page from '../../components/Page';
import PageTitle from '../../components/PageHeading';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import usePayoutSelector from '../../store/Slice/Payout/PayoutSelector';
import {
  getBankDropdownData,
  getCityData,
  getLoanTypeData,
  saveApiData,
  savefileApiData,
  downloadApiData
} from '../../store/Slice/Global/GlobalSlice';
import useGlobalSelector from '../../store/Slice/Global/GlobalSelector';
import {
  getAdvancePendingViewList,
  getPayoutCommentLogs,
  getPayoutViewData
} from '../../store/Slice/Payout/PayoutSlice';
import { getRevenuePartnerDetails } from '../../store/Slice/Revenue/RevenueSlice';
import ViewCardComponent from '../../components/ViewCards';
import { normalizeValue } from '../../utils/CommonFunction';
import ViewLargeCard from '../../components/ViewLargeCard';
import { StyledIconButton, StyledTableRow } from '../../utils/StyleTable';
import { PayoutApi } from '../../utils/Apis';
import CustomInput from '../../components/CustomInput';
import CustomSelect from '../../components/CustomSelect';
import { setApiError } from '../../store/Slice/Error/ErrorSlice';
import { UserMoreMenu } from '../../components/_dashboard/user';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    fontSize: 12,
    padding: '6px',
    height: '42.5px',
    fontWeight: 500,
    textTransform: 'uppercase',
    color: '#0d4689'
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 11,
    padding: '4px 6px',
    height: '42.5px'
  }
}));

export const addPayoutValidationSchema = yup.object().shape({
  fileNumber: yup
    .string()
    .required('Please add file no')
    .max(50, 'File No must be less than 50 characters'),

  fileSubmissionDate: yup.string().required('Please add submission date'),

  bankId: yup
    .string()
    .nullable()
    .required('Bank Name is required')
    .notOneOf(['', ' ', null], 'Please select a valid Bank'),
  cityId: yup
    .string()
    .nullable()
    .required('City Name is required')
    .notOneOf(['', ' ', null], 'Please select a valid City'),

  borrowerName: yup
    .string()
    .required('Please add borrower name')
    .max(50, 'Borrower Name must be less than 50 characters'),

  loanTypeId: yup
    .string()
    .required('Please select loan type')
    .notOneOf(['', ' ', null], 'Please select a valid Loan Type'),

  disburseMonth: yup.string().required('Please add disbursed month'),

  disburseAmount: yup
    .number()
    .typeError('Please add disbursed amount')
    .required('Please add disbursed amount')
    .positive('Disbursed Amount must be greater than 0')
    .max(10000000000, 'Please add disbursed amount less than 10,00,00,00,000'),

  sanctionedMonth: yup.string().required('Please add sanctioned month'),

  sanctionedAmount: yup
    .number()
    .typeError('Please add sanctioned amount')
    .required('Please add sanctioned amount')
    .positive('Sanctioned Amount must be greater than 0')
    .max(10000000000, 'Please add sanctioned amount less than 10,00,00,00,000'),

  encashmentMonth: yup.string().required('Please add encashment month'),

  encashmentAmount: yup
    .number()
    .typeError('Please add encashment amount')
    .required('Please add encashment amount')
    .positive('Encashment Amount must be greater than 0')
    .max(10000000000, 'Please add encashment amount less than 10,00,00,00,000')
});

function ViewPayout() {
  const [openSendMsg, setOpenSendMsg] = useState(false);
  const [editFile, setEditFile] = useState(false);
  const [fullWidth, setFullWidth] = useState(true);
  const [maxWidth, setMaxWidth] = useState('md');
  const [viewMorePopup, setViewMorePopup] = useState(false);
  const [addNewComment, setAddNewComment] = useState('');
  const [completeCommentMessage, setCompleteCommentMessage] = useState('');
  const fileInputRef = useRef(null);
  const [sanctionedFile, setSanctionedFile] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [fileUploaded, setFileUploaded] = useState(false);
  const [invoicePdfUrl, setInvoicePdfUrl] = useState(null);
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  const [openPdfDialog, setOpenPdfDialog] = useState(false);

  const {
    handleSubmit: handleReduxSubmit,
    control,
    reset,
    setValue,
    formState: { errors }
  } = useForm({
    mode: 'onBlur', // Add this to validate on blur
    defaultValues: {
      fileNumber: '',
      fileSubmissionDate: '',
      bankId: '',
      cityId: '',
      borrowerName: '',
      loanTypeId: '',
      disburseMonth: '',
      disburseAmount: '',
      sanctionedMonth: '',
      sanctionedAmount: '',
      encashmentMonth: '',
      encashmentAmount: ''
    }
  });

  const location = useLocation();
  const { rowId } = location?.state || {};
  // console.log('sss rowId data', rowId);
  const dispatch = useDispatch();
  const { LoginData } = useLoginSelector();
  // console.log('LoginData in payout view page', LoginData);
  const { PayoutCommentLog, PayoutViewData, AdvancePendingViewData } = usePayoutSelector();
  const { LoanTypeDrop, BankDrop, CityDrop } = useGlobalSelector();

  // console.log('PayoutViewData222', PayoutViewData);
  // console.log('AdvancePendingViewData', AdvancePendingViewData);

  const handleEditOpen = (e) => {
    setEditFile(true);
    reset();
    setValue('fileNumber', PayoutViewData?.fileNumber || '');
    setValue('fileSubmissionDate', PayoutViewData?.fileSubmissionDate || '');
    setValue('bankId', PayoutViewData?.bankId || null);
    setValue('cityId', PayoutViewData?.cityId || null);
    setValue('borrowerName', PayoutViewData?.borrowerName || '');
    setValue('loanTypeId', PayoutViewData?.loanTypeId || '');
    setValue('disburseMonth', PayoutViewData?.disburseMonth || '');
    setValue('disburseAmount', PayoutViewData?.disburseAmount || '');
    setValue('sanctionedMonth', PayoutViewData?.sanctionedMonth || '');
    setValue('sanctionedAmount', PayoutViewData?.sanctionedAmount || '');
    setValue('encashmentMonth', PayoutViewData?.encashmentMonth || '');
    setValue('encashmentAmount', PayoutViewData?.encashmentAmount || '');
  };

  const onSubmit = useCallback(
    async (data) => {
      const finalData = {
        ...data,
        companyId: LoginData?.companyId,
        userId: LoginData?.userId,
        employeeClass: 'Others',
        partnerId: null,
        applicationId: rowId?.applicationId ? rowId?.applicationId : null,
        partnerPayoutId: rowId?.partnerPayoutId ? rowId?.partnerPayoutId : null
      };
      try {
        const res = await dispatch(saveApiData(PayoutApi?.addupdatePayout, finalData));
        if (res?.status === 'success') {
          getPayoutViewApi();
          setEditFile(false);
        }
      } catch (error) {
        console.error('Error submitting form:', error);
      }
    },
    [LoginData] // 🔹 depends on LoginData
  );

  const handleSanctionedLetterUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSanctionedFile(file);
    setUploadingFile(true);
    setFileUploaded(false);
    try {
      const formData = new FormData();
      formData.append('partnerPayoutId', rowId?.partnerPayoutId || '');
      formData.append('companyId', LoginData?.companyId || '');
      formData.append('userId', LoginData?.userId || '');
      formData.append('file', file);
      const res = await dispatch(savefileApiData(PayoutApi?.uploadSanctionedLetter, formData));
      if (res) {
        setFileUploaded(true);
      }
    } catch (error) {
      console.error('Sanctioned letter upload error:', error);
    } finally {
      setUploadingFile(false);
    }
  };

  const fetchInvoicePdf = useCallback(async () => {
    const pId =
      PayoutViewData?.paybleId ||
      PayoutViewData?.payableId ||
      PayoutViewData?.invoiceId ||
      PayoutViewData?.partnerPayoutId;
    if (!pId || !LoginData) return;
    setLoadingInvoice(true);
    const credentials = {
      userId: LoginData?.userId,
      companyId: LoginData?.companyId,
      paybleIds: [pId],
      filterModel: {
        partnerId: null,
        bankId: null,
        invoiceNo: null,
        status: null,
        fromDate: null,
        toDate: null
      }
    };
    try {
      const result = await dispatch(
        downloadApiData(PayoutApi?.payableDownload || 'payble/download-invoice-pdf', credentials)
      );
      if (result?.success && result?.data) {
        const url = window.URL.createObjectURL(result.data);
        setInvoicePdfUrl(url);
      }
    } catch (err) {
      console.error('Invoice PDF fetch error:', err);
    } finally {
      setLoadingInvoice(false);
    }
  }, [PayoutViewData, LoginData, dispatch]);

  useEffect(() => {
    if (PayoutViewData) {
      fetchInvoicePdf();
    }
  }, [PayoutViewData, fetchInvoicePdf]);

  useEffect(
    () => () => {
      if (invoicePdfUrl) {
        URL.revokeObjectURL(invoicePdfUrl);
      }
    },
    [invoicePdfUrl]
  );

  const handleAddComment = async (e) => {
    if (addNewComment === '') {
      dispatch(setApiError('Please write your comments'));
      return;
    }

    const credentials = {
      comment: addNewComment,
      commentPutBy: LoginData?.userId,
      partnerPayoutId: rowId !== null ? rowId.partnerPayoutId : null
    };
    try {
      const res = await dispatch(saveApiData(PayoutApi?.payoutAddComment, credentials));
      console.log('res after adding comment', res);
      if (res?.status === 'success') {
        getPayoutViewApi();
        dispatch(getPayoutCommentLogs(rowId?.partnerPayoutId));
        setOpenSendMsg(false);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  // loan type & Bank Dropdown
  const getLoanTypeBankDropAPi = useCallback(() => {
    if (!LoginData) return;
    const data = {
      companyId: LoginData?.companyId,
      userId: LoginData?.userId
    };
    dispatch(getLoanTypeData(data));
    dispatch(getBankDropdownData(data));
  }, [LoginData]);

  // payout comment log
  const getPayoutCommentLogApi = useCallback(() => {
    if (!rowId) return;
    dispatch(getPayoutCommentLogs(rowId?.partnerPayoutId));
  }, [rowId]);

  // payout view
  const getPayoutViewApi = useCallback(() => {
    if (!rowId || !LoginData) return;
    const credentials = {
      applicationId: rowId?.applicationId,
      companyId: LoginData?.companyId,
      partnerPayoutId: rowId?.partnerPayoutId,
      userId: LoginData?.userId
    };
    dispatch(getPayoutViewData(credentials));
  }, [rowId, LoginData]);

  const getAdvancePendingViewApi = useCallback(() => {
    if (!rowId || !LoginData) return;
    const credentials = {
      payoutId: rowId?.partnerPayoutId,
      userId: LoginData?.userId
    };
    dispatch(getAdvancePendingViewList(credentials));
  }, [rowId, LoginData]);

  // city api
  const handleGetState = (optionVal) => {
    const data = {
      stateId: optionVal
    };
    dispatch(getCityData(data)); // city data
  };

  useEffect(() => {
    handleGetState(null); // city api
    dispatch(getRevenuePartnerDetails()); // partner details
  }, []);

  useEffect(() => {
    getPayoutCommentLogApi();
  }, [getPayoutCommentLogApi]);

  useEffect(() => {
    getLoanTypeBankDropAPi();
  }, [getLoanTypeBankDropAPi]);

  useEffect(() => {
    getPayoutViewApi();
  }, [getPayoutViewApi]);

  useEffect(() => {
    getAdvancePendingViewApi();
  }, [getAdvancePendingViewApi]);

  // ✅ Commission Details fields (memoized)
  const commissionDetailsFields = useMemo(
    () => [
      {
        icon: <GiTakeMyMoney className="viewLabelIcon" size={24} />,
        label: 'Amount Sanctioned',
        value: normalizeValue(PayoutViewData?.sanctionedAmount, NOT_AVAILABLE)
      },
      {
        icon: <GiPayMoney className="viewLabelIcon" size={24} />,
        label: 'Amount Disbursed',
        value: normalizeValue(PayoutViewData?.disburseAmount, NOT_AVAILABLE)
      },
      {
        icon: <AdUnitsIcon className="viewLabelIcon" size={24} />,
        label: 'Platform Commission',
        value: normalizeValue(PayoutViewData?.platformCommission, NOT_AVAILABLE)
      },
      {
        icon: <GroupIcon className="viewLabelIcon" size={24} />,
        label: 'Partner Commission',
        value: normalizeValue(PayoutViewData?.partnerCommission, NOT_AVAILABLE)
      },
      {
        icon: <ReceiptIcon className="viewLabelIcon" size={24} />,
        label: 'Invoice Amount',
        value: normalizeValue(PayoutViewData?.invoiceAmount, NOT_AVAILABLE)
      },
      {
        icon: <CalendarTodayIcon className="viewLabelIcon" size={24} />,
        label: 'Likely Payment Date',
        value: normalizeValue(PayoutViewData?.likelyPaymentDate, NOT_AVAILABLE)
      },
      {
        icon: <GiTakeMyMoney className="viewLabelIcon" size={24} />,
        label: 'Net Revenue',
        value: normalizeValue(PayoutViewData?.netRevenue, NOT_AVAILABLE)
      }
    ],
    [PayoutViewData]
  );

  // ✅ Payment Details fields (memoized)
  const paymentDetailsFields = useMemo(
    () => [
      {
        icon: <CalendarTodayIcon className="viewLabelIcon" size={24} />,
        label: 'Payment Date',
        value: normalizeValue(PayoutViewData?.paymentDate, NOT_AVAILABLE)
      },
      {
        icon: <GiMoneyStack className="viewLabelIcon" size={24} />,
        label: 'Gross Amount',
        value: normalizeValue(PayoutViewData?.grossAmount, NOT_AVAILABLE)
      },
      {
        icon: <GiPayMoney className="viewLabelIcon" size={24} />,
        label: 'Amount Paid',
        value: normalizeValue(PayoutViewData?.amountPaid, NOT_AVAILABLE)
      },
      {
        icon: <TbReceiptTax className="viewLabelIcon" size={24} />,
        label: 'TDS Deduction',
        value: normalizeValue(PayoutViewData?.tdsDeduction, NOT_AVAILABLE)
      },
      {
        icon: <HiReceiptTax className="viewLabelIcon" size={24} />,
        label: 'GST Paid Amount',
        value: normalizeValue(PayoutViewData?.gstPaidAmount, NOT_AVAILABLE)
      },
      {
        icon: <AddCardIcon className="viewLabelIcon" size={24} />,
        label: 'Payment Mode',
        value: normalizeValue(PayoutViewData?.paymentMode, NOT_AVAILABLE)
      }
    ],
    [PayoutViewData]
  );

  const payoutFields = useMemo(
    () => [
      {
        label: 'Application ID',
        value: PayoutViewData?.applicationId,
        icon: <EventNoteIcon className="viewLabelIcon" size={24} />
      },
      {
        label: 'File Number',
        value: PayoutViewData?.fileNumber,
        icon: <MdOutlineDateRange className="viewLabelIcon" size={24} />
      },
      {
        label: 'Submission Date',
        value: PayoutViewData?.fileSubmissionDate ? fDate(PayoutViewData.fileSubmissionDate) : '',
        icon: <EventNoteIcon className="viewLabelIcon" size={24} />
      },

      {
        label: 'Bank',
        value: PayoutViewData?.bankName,
        icon: <EventNoteIcon className="viewLabelIcon" size={24} />
      },
      {
        label: 'City',
        value: PayoutViewData?.cityName,
        icon: <CalendarTodayIcon className="viewLabelIcon" size={24} />
      },
      {
        label: 'Borrower Name',
        value: PayoutViewData?.borrowerName,
        icon: <FaRegUser className="viewLabelIcon" size={24} />
      },
      {
        label: 'Loan Type',
        value: PayoutViewData?.loanName,
        icon: <GroupIcon className="viewLabelIcon" size={24} />
      },
      {
        label: 'Disbursed Month',
        value: PayoutViewData?.disburseMonth,
        icon: <MdOutlineDateRange className="viewLabelIcon" size={24} />
      },
      {
        label: 'Disbursed Amount',
        value: PayoutViewData?.disburseAmount,
        icon: <MdOutlineDateRange className="viewLabelIcon" size={24} />
      },
      {
        label: 'Source Flag',
        value: PayoutViewData?.sourceFlag,
        icon: <MdOutlineDateRange className="viewLabelIcon" size={24} />
      },
      {
        label: 'Partner',
        value: PayoutViewData?.partnerName,
        icon: <EventNoteIcon className="viewLabelIcon" size={24} />
      },
      {
        label: 'Current Status',
        value: PayoutViewData?.currentStatus,
        icon: <MdOutlineDateRange className="viewLabelIcon" size={24} />
      },
      {
        label: 'Last Updated',
        value: PayoutViewData?.fileLastUpdated ? fDate(PayoutViewData.fileLastUpdated) : '',
        icon: <MdOutlineDateRange className="viewLabelIcon" size={24} />
      }

      // {
      //   label: 'Loan Account Number',
      //   value: PayoutViewData?.fileNumber,
      //   icon: <MdOutlineDateRange className="viewLabelIcon" size={24} />
      // },

      // {
      //   label: 'Sanctioned Amount',
      //   value: PayoutViewData?.sanctionedAmount,
      //   icon: <MdOutlineDateRange className="viewLabelIcon" size={24} />
      // },
      // {
      //   label: 'SM Contact Name',
      //   value: PayoutViewData?.smContactName,
      //   icon: <MdOutlineDateRange className="viewLabelIcon" size={24} />
      // },
      // {
      //   label: 'SM Mobile Number',
      //   value: PayoutViewData?.smMobileNumber,
      //   icon: <MdOutlineDateRange className="viewLabelIcon" size={24} />
      // },
      // {
      //   label: 'SM Email',
      //   value: PayoutViewData?.smEmail,
      //   icon: <MdOutlineDateRange className="viewLabelIcon" size={24} />
      // },

      // {
      //   label: 'Company Name',
      //   value: PayoutViewData?.companyName,
      //   icon: <MdOutlineDateRange className="viewLabelIcon" size={24} />
      // },
      // {
      //   label: 'DSA Name',
      //   value: PayoutViewData?.dsaName,
      //   icon: <MdOutlineDateRange className="viewLabelIcon" size={24} />
      // },

      // {
      //   label: 'Source Type',
      //   value: PayoutViewData?.sourceType,
      //   icon: <MdOutlineDateRange className="viewLabelIcon" size={24} />
      // },
      // {
      //   label: 'Current Stage',
      //   value: PayoutViewData?.currentStage,
      //   icon: <MdOutlineDateRange className="viewLabelIcon" size={24} />
      // },

      // {
      //   label: 'Login Profile Id',
      //   value: PayoutViewData?.loginProfileId,
      //   icon: <MdOutlineDateRange className="viewLabelIcon" size={24} />
      // }
    ],
    [PayoutViewData]
  );
  const handlePendingStatus = async (data, status) => {
    console.log('item and data', data, status);
    const credentials = {
      partnerAdvanceId: data?.partnerAdvanceId,
      userId: LoginData?.userId,
      newStatus: status,
      remarks: 'Done'
    };
    const result = await dispatch(saveApiData(PayoutApi?.advancePendingStatus, credentials));
    if (result?.approvalStatus === 'REJECTED') {
      dispatch(setApiError('Advance File Rejected'));
      getPayoutViewApi();
    } else if (result?.approvalStatus === 'APPROVED') {
      dispatch(setApiError('Advance File Approved'));
      getPayoutViewApi();
    }
    // console.log('result222', result);
  };

  return (
    <Page title="Partner Management System || Payout">
      <Container maxWidth="xl">
        <Box pt={3} pb={3} mb={4}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            className="pageHeading"
          >
            <PageTitle info="View File" />
            <Box>
              <BrudCrumbs />
            </Box>
          </Stack>

          <Box container textAlign="right">
            <Box className="statusBox">
              <Typography variant="body2">Current Status:</Typography>
              <Typography variant="subtitle2" className="disbursedText">
                {PayoutViewData !== null && PayoutViewData.payoutStatus
                  ? PayoutViewData.payoutStatus
                  : `${NOT_AVAILABLE}`}
              </Typography>
            </Box>
          </Box>
          <ViewLargeCard
            title="File Details"
            fields={payoutFields || []}
            handleEdit={handleEditOpen}
          />
          <Grid container spacing={3} mb={3}>
            <ViewCardComponent title="Commission Details" fields={commissionDetailsFields} />
            {/* Uncomment when you want to show payment details */}
            {/* <ViewCardComponent title="Payment Details" fields={paymentDetailsFields} /> */}
          </Grid>
          <Typography variant="body1" className="headingLineUp headingLineUpOut">
            Advance
          </Typography>
          <Box mb={3} className="viewLeadCard cardTopMinusMargin viewCardPadding">
            {AdvancePendingViewData?.length > 0 ? (
              <TableContainer sx={{ minWidth: 800, overflow: 'auto', background: 'white' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>partner Id</StyledTableCell>
                      <StyledTableCell align="">Payout Id</StyledTableCell>
                      <StyledTableCell align="">Request Amount</StyledTableCell>
                      <StyledTableCell align="">Approval Status</StyledTableCell>
                      <StyledTableCell align="">Purpose</StyledTableCell>
                      <StyledTableCell align="">Request Date</StyledTableCell>
                      <StyledTableCell align="">Action</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {AdvancePendingViewData &&
                      AdvancePendingViewData?.map((item, index) => (
                        <StyledTableRow key={index}>
                          <StyledTableCell align="">
                            {item?.partnerId ? item?.partnerId : `${NOT_AVAILABLE}`}
                          </StyledTableCell>
                          <StyledTableCell align="">
                            {item.payoutId ? item.payoutId : `${NOT_AVAILABLE}`}
                          </StyledTableCell>
                          <StyledTableCell align="">
                            {item.requestAmount ? item.requestAmount : `${NOT_AVAILABLE}`}
                          </StyledTableCell>
                          <StyledTableCell align="">
                            {item.approvalStatus ? item.approvalStatus : `${NOT_AVAILABLE}`}
                          </StyledTableCell>
                          <StyledTableCell align="">
                            {item.purpose ? item.purpose : `${NOT_AVAILABLE}`}
                          </StyledTableCell>
                          <StyledTableCell align="">
                            {item.requestDate ? item.requestDate : `${NOT_AVAILABLE}`}
                          </StyledTableCell>
                          <StyledTableCell align="" width={80}>
                            <UserMoreMenu
                              statusCode={handlePendingStatus}
                              rowId={item}
                              parent="advance-pending"
                            />
                          </StyledTableCell>
                        </StyledTableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box>
                <Typography variant="body2" align="center">
                  No Advance Found
                </Typography>
              </Box>
            )}

            <Dialog open={viewMorePopup} onClose={(e) => setViewMorePopup(false)}>
              <DialogContent dividers>
                <Typography variant="body2">{completeCommentMessage}</Typography>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={(e) => setViewMorePopup(false)}
                  variant="contained"
                  className="mainBtn"
                >
                  Close
                </Button>
              </DialogActions>
            </Dialog>
          </Box>

          <Typography variant="body1" className="headingLineUp headingLineUpOut">
            Queries
          </Typography>
          <Box className="viewLeadCard cardTopMinusMargin viewCardPadding">
            <Box className="extraTopIcon">
              <Tooltip
                title="Send Message"
                placement="top"
                arrow
                onClick={(e) => {
                  setOpenSendMsg(true);
                }}
              >
                <StyledIconButton className="squareIconButton squareIconButtonFilled">
                  <MdOutlineSendToMobile size={14} />
                </StyledIconButton>
              </Tooltip>
            </Box>
            {PayoutCommentLog?.length > 0 ? (
              <TableContainer sx={{ minWidth: 800, overflow: 'auto', background: 'white' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>Date</StyledTableCell>
                      <StyledTableCell align="">Person Name</StyledTableCell>
                      <StyledTableCell align="">Comments</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {PayoutCommentLog?.length > 0 &&
                      PayoutCommentLog?.map((item, index) => {
                        let commentDetailsStr = item.commentDetails;
                        if (commentDetailsStr && commentDetailsStr.length > 20) {
                          commentDetailsStr = commentDetailsStr.substring(0, 20);
                        }
                        return (
                          <StyledTableRow>
                            <StyledTableCell align="">
                              {item.commentLogDate && item.commentLogDate !== ''
                                ? fDate(item.commentLogDate)
                                : `${NOT_AVAILABLE}`}
                            </StyledTableCell>
                            <StyledTableCell align="">
                              {item.personName ? item.personName : `${NOT_AVAILABLE}`}
                            </StyledTableCell>
                            <StyledTableCell align="">
                              {commentDetailsStr ? commentDetailsStr : `${NOT_AVAILABLE}`}
                              <Tooltip title="More" placement="top" arrow>
                                <IconButton
                                  size="small"
                                  variant="subtitle2"
                                  onClick={(e) => {
                                    setViewMorePopup(true);
                                    setCompleteCommentMessage(item.commentDetails);
                                  }}
                                >
                                  <MdOutlineMoreHoriz size={16} />
                                </IconButton>
                              </Tooltip>
                            </StyledTableCell>
                          </StyledTableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box>
                <Typography variant="body2" align="center">
                  No Queries Found
                </Typography>
              </Box>
            )}

            <Dialog open={viewMorePopup} onClose={(e) => setViewMorePopup(false)}>
              <DialogContent dividers>
                <Typography variant="body2">{completeCommentMessage}</Typography>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={(e) => setViewMorePopup(false)}
                  variant="contained"
                  className="mainBtn"
                >
                  Close
                </Button>
              </DialogActions>
            </Dialog>
          </Box>

          <Typography variant="body1" className="headingLineUp headingLineUpOut" sx={{ mt: 3 }}>
            Partner Payout Details
          </Typography>
          <Box className="viewLeadCard cardTopMinusMargin viewCardPadding" mb={3}>
            {loadingInvoice ? (
              <Box display="flex" justifyContent="center" alignItems="center" py={3}>
                <CircularProgress size={30} />
              </Box>
            ) : invoicePdfUrl ? (
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 1.5, fontWeight: 500, color: 'text.secondary' }}
                >
                  Invoice Tax Document
                </Typography>
                <Tooltip title="Click to view full Invoice PDF" placement="top" arrow>
                  <Box
                    onClick={() => setOpenPdfDialog(true)}
                    sx={{
                      width: '120px',
                      height: '150px',
                      border: '1px solid #dcdcdc',
                      borderRadius: '8px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      bgcolor: '#fafafa',
                      p: 1.5,
                      boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                        borderColor: 'primary.main'
                      }
                    }}
                  >
                    <iframe
                      src={invoicePdfUrl}
                      title="Invoice Preview"
                      style={{
                        width: '100%',
                        height: '100px',
                        border: 'none',
                        pointerEvents: 'none',
                        overflow: 'hidden'
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        mt: 1,
                        fontSize: '11px',
                        color: 'text.secondary',
                        fontWeight: 500,
                        textTransform: 'uppercase'
                      }}
                    >
                      Tax Invoice
                    </Typography>
                  </Box>
                </Tooltip>

                {/* Full Screen / Large Popup Modal */}
                <Dialog
                  open={openPdfDialog}
                  onClose={() => setOpenPdfDialog(false)}
                  fullWidth
                  maxWidth="md"
                >
                  <DialogTitle
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    <Typography variant="h6">Tax Invoice Preview</Typography>
                    <IconButton onClick={() => setOpenPdfDialog(false)} size="small">
                      <CloseIcon />
                    </IconButton>
                  </DialogTitle>
                  <DialogContent dividers sx={{ p: 0, height: '70vh' }}>
                    <iframe
                      src={invoicePdfUrl}
                      width="100%"
                      height="100%"
                      title="Full Invoice PDF"
                      style={{ border: 'none' }}
                    />
                  </DialogContent>
                  <DialogActions>
                    <Button
                      onClick={() => setOpenPdfDialog(false)}
                      variant="contained"
                      className="mainBtn"
                    >
                      Close
                    </Button>
                  </DialogActions>
                </Dialog>
              </Box>
            ) : (
              <Box py={2} textAlign="center">
                <Typography variant="body2" color="text.secondary">
                  No invoice PDF available
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        <Dialog
          className="PopupBox"
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          fullWidth={fullWidth}
          maxWidth={maxWidth}
          open={editFile}
        >
          <DialogTitle className="popupTitle">
            <Typography variant="h6">Edit File</Typography>
            <IconButton onClick={(e) => setEditFile(false)} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <form id="updatePayout" onSubmit={handleReduxSubmit(onSubmit)}>
              <Grid container spacing={3} mb={3}>
                <Grid item xs={4}>
                  <CustomInput
                    name="fileNumber"
                    label="File No"
                    control={control}
                    errors={errors}
                    isRequired
                    fieldCode="FILE_NO"
                    fieldType="TEXT_FIELD"
                  />
                </Grid>
                <Grid item xs={4}>
                  <CustomInput
                    name="fileSubmissionDate"
                    label="Submission Date"
                    type="date"
                    control={control}
                    errors={errors}
                    isRequired
                    fieldCode="DOB"
                    fieldType="DATE_FIELD"
                  />
                </Grid>
                <Grid item xs={4}>
                  <CustomSelect
                    name="bankId"
                    label="Bank Name"
                    control={control}
                    errors={errors}
                    options={BankDrop || []}
                    isRequired
                  />
                </Grid>
                <Grid item xs={4}>
                  <CustomSelect
                    name="cityId"
                    label="City"
                    control={control}
                    errors={errors}
                    options={CityDrop || []}
                    isRequired
                  />
                </Grid>
                <Grid item xs={4}>
                  <CustomInput
                    name="borrowerName"
                    label="Borrower Name"
                    control={control}
                    errors={errors}
                    isRequired
                    fieldCode="NAME"
                    fieldType="ALPHABET"
                  />
                </Grid>
                <Grid item xs={4}>
                  <CustomSelect
                    name="loanTypeId"
                    label="Loan Type"
                    control={control}
                    errors={errors}
                    options={LoanTypeDrop || []}
                    isRequired
                  />
                </Grid>
                <Grid item xs={4}>
                  <CustomInput
                    name="disburseMonth"
                    label="Disbursed Month"
                    type="month"
                    control={control}
                    errors={errors}
                    isRequired
                    fieldCode="DATE"
                    fieldType="DATE_FIELD"
                  />
                </Grid>
                <Grid item xs={4}>
                  <CustomInput
                    name="disburseAmount"
                    label="Disbursed Amount"
                    type="Number"
                    control={control}
                    errors={errors}
                    isRequired
                    // fieldCode="AMOUNT"
                    fieldType="AMOUNT_FIELD"
                  />
                </Grid>
                <Grid item xs={4}>
                  <CustomInput
                    name="sanctionedMonth"
                    label="Sanctioned Month"
                    type="month"
                    control={control}
                    errors={errors}
                    isRequired
                    fieldCode="DATE"
                    fieldType="DATE_FIELD"
                  />
                </Grid>
                <Grid item xs={4}>
                  <CustomInput
                    name="sanctionedAmount"
                    label="Sanctioned Amount"
                    type="Number"
                    control={control}
                    errors={errors}
                    isRequired
                    // fieldCode="AMOUNT"
                    fieldType="AMOUNT_FIELD"
                  />
                </Grid>
                {/* Sanctioned Letter Doc Upload */}
                <Grid item xs={4}>
                  <Box>
                    <Typography variant="h6" className="formLabel">
                      Add Sanctioned Letter Doc
                    </Typography>
                    <Tooltip
                      title={
                        fileUploaded
                          ? `Uploaded: ${sanctionedFile?.name || 'File Selected'}`
                          : 'Click to upload PDF / JPG / PNG'
                      }
                      placement="top"
                      arrow
                    >
                      <Box
                        className="textInput"
                        onClick={() => fileInputRef.current?.click()}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          cursor: 'pointer',
                          border: fileUploaded ? '1px solid #2e7d32' : '1px solid #d0d0d0',
                          borderRadius: '4px',
                          px: 1.5,
                          py: '9px',
                          bgcolor: fileUploaded ? '#f6fff7' : '#fff',
                          minHeight: '40px',
                          transition: 'border-color 0.2s',
                          '&:hover': {
                            borderColor: fileUploaded ? '#2e7d32' : '#888'
                          }
                        }}
                      >
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          style={{ display: 'none' }}
                          onChange={handleSanctionedLetterUpload}
                        />
                        {uploadingFile ? (
                          <CircularProgress size={16} sx={{ color: 'primary.main' }} />
                        ) : fileUploaded ? (
                          <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18 }} />
                        ) : (
                          <BiCloudUpload size={18} style={{ color: '#aaa' }} />
                        )}
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '13px',
                            color: fileUploaded ? '#2e7d32' : '#999',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flex: 1
                          }}
                        >
                          {uploadingFile
                            ? 'Uploading...'
                            : fileUploaded
                            ? sanctionedFile?.name || 'File Selected'
                            : 'Upload document'}
                        </Typography>
                      </Box>
                    </Tooltip>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <CustomInput
                    name="encashmentMonth"
                    label="Encashment Month"
                    type="month"
                    control={control}
                    errors={errors}
                    isRequired
                    fieldCode="DATE"
                    fieldType="DATE_FIELD"
                  />
                </Grid>
                <Grid item xs={4}>
                  <CustomInput
                    name="encashmentAmount"
                    label="Encashment Amount"
                    type="Number"
                    control={control}
                    errors={errors}
                    isRequired
                    // fieldCode="AMOUNT"
                    fieldType="AMOUNT_FIELD"
                  />
                </Grid>
              </Grid>{' '}
              <Button
                type="submit" // ✅ important
                variant="contained"
                className="mainBtn"
              >
                Submit
              </Button>
            </form>
          </DialogContent>
          {/* <DialogActions></DialogActions> */}
        </Dialog>

        <Dialog open={openSendMsg} fullWidth>
          <DialogTitle className="popupTitle">
            <Typography variant="h6">Add New Message</Typography>
            <IconButton
              onClick={(e) => {
                setOpenSendMsg(false);
                setAddNewComment('');
              }}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Box>
              <FormControl fullWidth>
                <TextField
                  type="text"
                  variant="outlined"
                  placeholder="Write Your Comments"
                  fullWidth
                  multiline
                  rows={5}
                  value={addNewComment}
                  onChange={(e) => setAddNewComment(e.target.value)}
                />
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={(e) => {
                handleAddComment(e);
              }}
              variant="contained"
              className="mainBtn"
            >
              Submit
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Page>
  );
}
export default ViewPayout;
